// @flow
import React, { useEffect, useState } from 'react';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock-upgrade';
import { t } from 'javascripts/utils/withTranslation';
import randomString from 'javascripts/utils/random-id';
import ErrorBoundary from 'javascripts/utils/ErrorBoundary';
import Burger from './Burger';
import Search from './Search';
import BurgerFallBack from './BurgerFallback';
import SearchFallBack from './SearchFallback';
import type { ExternalProps } from '../types';
import useMediaquery from '../../../../javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from '../../../../javascripts/constants';
import Login from './Login';

const Service = ({
  labelId,
  isMainNav,
  loginProps,
  ...props
}: {
  ...ExternalProps,
  labelId?: string,
  isMainNav?: boolean,
}): React$MixedElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const isDesktop = useMediaquery(MEDIA_QUERIES.xxl);
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);

  // Close every other overlay that listens to the overlayClose event on document
  const throwOverlayCloseEvent = () => {
    const overlayCloseEvent = new CustomEvent('overlayClose', {
      bubbles: true,
      detail: { target: trigger },
    });

    document.dispatchEvent(overlayCloseEvent);
  };

  const handleSearchOpen = (open: boolean) => {
    if (open) {
      setTrigger(document.activeElement);
      throwOverlayCloseEvent();
    }

    if (open && !isDesktop) {
      setTrigger(document.activeElement);
      throwOverlayCloseEvent();
    } else {
      clearAllBodyScrollLocks();
      trigger?.focus();
    }

    setIsOpen(false);
    setSearchIsOpen(open);
  };

  const handleBurgerOpen = (open: boolean) => {
    if (open) {
      setTrigger(document.activeElement);
      throwOverlayCloseEvent();
    } else {
      clearAllBodyScrollLocks();
      trigger?.focus();
    }
    setSearchIsOpen(false);
    setIsOpen(open);
  };

  const closeModals = (externalClose: boolean = false) => {
    setSearchIsOpen(false);
    setIsOpen(false);

    if (!externalClose) {
      clearAllBodyScrollLocks();
    }
  };

  useEffect(() => {
    document.addEventListener('overlayClose', (e: any) => {
      if (e.detail?.target !== trigger) {
        closeModals(true);
      }
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModals();
      }
    });
  }, []);

  if (isDesktop && !props.showBurgerDesktop && !loginProps) {
    return (
      <>
        <span id={labelId} className="u-hidden-visually">
          {t('Suchfunktion')}
        </span>
        <div className="header__service-list">
          <div className="header__search">
            <ErrorBoundary fallbackComponent={SearchFallBack}>
              <Search
                {...props}
                isOpen={searchIsOpen}
                setIsOpen={handleSearchOpen}
              />
            </ErrorBoundary>
          </div>
        </div>
      </>
    );
  }

  return (
    <ul
      className="header__service-list"
      aria-label={t('Inhaltsübersicht mit Suchfunktion')}
    >
      {isMainNav && loginProps && (
        <ErrorBoundary fallbackComponent={() => <></>}>
          <Login {...loginProps} />
        </ErrorBoundary>
      )}
      <li className="header__search">
        <ErrorBoundary fallbackComponent={SearchFallBack}>
          <Search
            {...props}
            isOpen={searchIsOpen}
            setIsOpen={handleSearchOpen}
          />
        </ErrorBoundary>
      </li>
      {(!isDesktop || props.showBurgerDesktop) && (
        <li className="header__burger">
          <ErrorBoundary fallbackComponent={BurgerFallBack}>
            <Burger
              {...props}
              isOpen={isOpen}
              setIsOpen={handleBurgerOpen}
              loginProps={loginProps}
            />
          </ErrorBoundary>
        </li>
      )}
    </ul>
  );
};

export default Service;
